import React from 'react';
import { AlertTriangle, Bell, Plus, TrendingUp, TrendingDown } from 'lucide-react';
import PageHeader from '../common/PageHeader';

const AlertsTab = () => {
  const alerts = [
    {
      id: 1,
      type: 'price_above',
      security: 'AAPL',
      condition: 'Price above $155.00',
      current_value: 150.25,
      target_value: 155.00,
      is_active: true,
      created_at: '2024-01-15'
    },
    {
      id: 2,
      type: 'price_below',
      security: 'TSLA',
      condition: 'Price below $180.00',
      current_value: 185.50,
      target_value: 180.00,
      is_active: true,
      created_at: '2024-01-14'
    },
    {
      id: 3,
      type: 'percent_change',
      security: 'SPY',
      condition: 'Daily change > +2%',
      current_value: 0.0030,
      target_value: 0.02,
      is_active: false,
      created_at: '2024-01-13'
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercent = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1
    }).format(value);
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'price_above':
        return TrendingUp;
      case 'price_below':
        return TrendingDown;
      case 'percent_change':
        return AlertTriangle;
      default:
        return Bell;
    }
  };

  const getAlertStatus = (alert) => {
    if (!alert.is_active) {
      return { status: 'Inactive', color: 'text-gray-500' };
    }

    if (alert.type === 'price_above' && alert.current_value >= alert.target_value) {
      return { status: 'Triggered', color: 'text-green-600' };
    }

    if (alert.type === 'price_below' && alert.current_value <= alert.target_value) {
      return { status: 'Triggered', color: 'text-red-600' };
    }

    if (alert.type === 'percent_change' && Math.abs(alert.current_value) >= alert.target_value) {
      return { status: 'Triggered', color: 'text-orange-600' };
    }

    return { status: 'Active', color: 'text-blue-600' };
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <PageHeader
        icon={Bell}
        title="Alerts"
        subtitle="Manage price and portfolio alerts"
      >
        <button
          type="button"
          className="inline-flex items-center p-2 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
        >
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">New Alert</span>
        </button>
      </PageHeader>

      <div className="max-w-7xl mx-auto py-6 px-3 sm:px-4 lg:px-8">

      {/* Alert Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-3 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Bell className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="ml-3 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total</dt>
                  <dd className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">{alerts.length}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-3 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-green-400 dark:text-green-500" />
              </div>
              <div className="ml-3 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Active</dt>
                  <dd className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                    {alerts.filter(a => a.is_active).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-3 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-orange-400 dark:text-orange-500" />
              </div>
              <div className="ml-3 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Triggered</dt>
                  <dd className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">0</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-3 sm:p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingDown className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="ml-3 sm:ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Inactive</dt>
                  <dd className="text-base sm:text-lg font-medium text-gray-900 dark:text-white">
                    {alerts.filter(a => !a.is_active).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {alerts.map((alert) => {
            const Icon = getAlertIcon(alert.type);
            const status = getAlertStatus(alert);

            return (
              <li key={alert.id}>
                <div className="px-3 py-3 sm:px-6 sm:py-4 hover:bg-gray-50 dark:hover:bg-gray-700">
                  {/* Mobile Layout */}
                  <div className="flex items-start justify-between gap-2 md:hidden">
                    <div className="flex items-start gap-2 flex-1 min-w-0">
                      <div className="flex-shrink-0 mt-0.5">
                        <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 mb-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {alert.security}
                          </p>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                            {status.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{alert.condition}</p>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Current: {alert.type.includes('price') ?
                            formatCurrency(alert.current_value) :
                            formatPercent(alert.current_value)
                          }
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button className="text-green-600 dark:text-green-500 hover:text-green-700 dark:hover:text-green-400 text-xs px-2 py-1">
                        Edit
                      </button>
                      <button className="text-red-600 dark:text-red-500 hover:text-red-700 dark:hover:text-red-400 text-xs px-2 py-1">
                        Delete
                      </button>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden md:flex items-center justify-between">
                    <div className="flex items-center flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        <Icon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                      <div className="ml-4 min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {alert.security}
                          </p>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                            {status.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{alert.condition}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {alert.type.includes('price') ?
                          formatCurrency(alert.current_value) :
                          formatPercent(alert.current_value)
                        }
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="text-green-600 dark:text-green-500 hover:text-green-700 dark:hover:text-green-400 text-sm">
                          Edit
                        </button>
                        <button className="text-red-600 dark:text-red-500 hover:text-red-700 dark:hover:text-red-400 text-sm">
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {alerts.length === 0 && (
        <div className="text-center py-12">
          <AlertTriangle className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No alerts</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new alert.</p>
          <div className="mt-6">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Alert
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default AlertsTab;